import { StyleSheet, View, ViewProps } from 'react-native'
import { Chip, FormField, Hyperlink, Select, Spacing, Text } from 'zens-storybook'
import { useTranslation } from 'react-i18next'
import {
  StorageKey,
  WorkspacesFilter,
  titleForProductConnectivityState,
  useStore,
  productConnectivityStates,
  productOccupancyStates,
  titleForProductOccupancyState,
  productPeripheralStates,
  titleForProductPeripheralState,
  useWorkspaceTypes,
  useBuildings,
  useFloors,
} from 'zens-store'
import { FloorSelect, ProductConnectivitySelect, ProductOccupancySelect, ProductPeripheralsSelect } from 'zens-fragment'

import { WorkspaceTypeSelect } from '../workspacetype'
import { FilterPanel } from '../filter'

const styles = StyleSheet.create({
  container: {
    zIndex: 10,
    marginBottom: Spacing.XS,
  },
  content: {
    marginVertical: -Spacing.XS,
  },
  field: {
    flex: 1,
    minWidth: 275,
    margin: Spacing.XS,
  },
  select: {
    zIndex: 20,
  },
  value: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    margin: -Spacing.XXS,
    alignItems: 'center',
  },
  chip: {
    margin: Spacing.XXS,
  },
  clearAll: {
    marginLeft: Spacing.XS,
  },
  workspaceType: {
    zIndex: 80,
  },
  floor: {
    zIndex: 70,
  },
  installed: {
    zIndex: 60,
  },
  occupancy: {
    zIndex: 50,
  },
  connectivity: {
    zIndex: 40,
  },
  peripherals: {
    zIndex: 30,
  },
})

interface Props extends ViewProps {
  companyId: number
  filter?: WorkspacesFilter
  onFilterChange?: (filter: WorkspacesFilter) => void
  loading?: boolean
}

const WorkspaceFilter = (props: Props) => {
  const { companyId, style, filter, onFilterChange, loading, ...otherProps } = props
  const { t } = useTranslation()
  const { storage } = useStore()
  const workspaceTypes = useWorkspaceTypes()
  const buildings = useBuildings({ companyId })
  const floors = useFloors({ companyId })
  const hasFilter = Object.keys(filter || {}).length > 0

  const workspaceTypeData = filter?.workspaceTypeId
    ?.map((workspaceTypeId) => workspaceTypes.data?.find((workspaceType) => workspaceType.id === workspaceTypeId))
    .sort((a, b) => a?.name?.localeCompare(b?.name || '') || 0)
    .map((workspaceType) => ({
      key: 'workspaceTypeId' as keyof WorkspacesFilter,
      value: workspaceType?.id,
      title: workspaceType?.name,
    }))

  const floorData = filter?.floorId
    ?.map((floorId) => floors.data?.find((floor) => floor.id === floorId))
    .map((floor) => {
      const building = buildings.data?.find((building) => building.id === floor?.buildingId)
      return { floor, building }
    })
    .sort(
      (a, b) =>
        a?.building?.name?.localeCompare(b?.building?.name || '') || (a?.floor?.number || 0) - (b?.floor?.number || 0),
    )
    .map(({ floor, building }) => ({
      key: 'floorId' as keyof WorkspacesFilter,
      value: floor?.id,
      title: `${building?.name}: ${floor?.displayName}`,
    }))

  const installedData =
    filter?.isInstalled !== undefined
      ? [
          {
            key: 'isInstalled' as keyof WorkspacesFilter,
            value: filter?.isInstalled,
            title: filter?.isInstalled ? t('Installed') : t('Not installed'),
          },
        ]
      : undefined

  const occupancyStateData = filter?.occupancyState
    ?.sort((a, b) => productOccupancyStates.indexOf(a) - productOccupancyStates.indexOf(b))
    .map((state) => ({
      key: 'occupancyState' as keyof WorkspacesFilter,
      value: state,
      title: titleForProductOccupancyState(state, t),
    }))

  const connectivityStateData = filter?.connectivityState
    ?.sort((a, b) => productConnectivityStates.indexOf(a) - productConnectivityStates.indexOf(b))
    .map((state) => ({
      key: 'connectivityState' as keyof WorkspacesFilter,
      value: state,
      title: titleForProductConnectivityState(state, t),
    }))

  const peripheralStateData = filter?.peripheralsState
    ?.sort((a, b) => productPeripheralStates.indexOf(a) - productPeripheralStates.indexOf(b))
    .map((state) => ({
      key: 'peripheralsState' as keyof WorkspacesFilter,
      value: state,
      title: titleForProductPeripheralState(state, t),
    }))

  const data = [
    ...(workspaceTypeData || []),
    ...(floorData || []),
    ...(installedData || []),
    ...(occupancyStateData || []),
    ...(connectivityStateData || []),
    ...(peripheralStateData || []),
  ]

  const value = hasFilter ? (
    <View style={styles.value}>
      {data.map((item) => (
        <Chip
          key={`${item.key}-${item.value}`}
          title={item.title}
          closeable
          style={styles.chip}
          onClose={removeFilter(
            item as { key: keyof Omit<WorkspacesFilter, 'search'>; value: number | string | boolean; title: string },
          )}
        />
      ))}
      <Hyperlink title={t('Clear filters')} onPress={clearFilter} style={styles.clearAll} />
    </View>
  ) : undefined

  function clearFilter() {
    onFilterChange?.(filter?.search ? { search: filter.search } : {})
    storage.delete(`${companyId}:${StorageKey.WorkspaceFilter}`, { session: true })
  }

  function removeFilter(item: {
    key: keyof Omit<WorkspacesFilter, 'search'>
    value: number | string | boolean
    title: string
  }) {
    return () => {
      const newFilter = { ...filter }
      if (item.key === 'isInstalled') {
        delete newFilter[item.key]
      } else {
        const newValue = newFilter[item.key]?.filter((value) => value !== item.value)
        if (newValue?.length) newFilter[item.key] = newValue
        else delete newFilter[item.key]
      }

      onFilterChange?.(newFilter)
      storage.set(`${companyId}:${StorageKey.WorkspaceFilter}`, JSON.stringify(newFilter), { session: true })
    }
  }

  function updateFilter(key: string, value: number[]) {
    const newFilter = { ...filter, [key]: value }
    onFilterChange?.(newFilter)
    storage.set(`${companyId}:${StorageKey.WorkspaceFilter}`, JSON.stringify(newFilter), { session: true })
  }

  return (
    <FilterPanel
      value={value}
      loading={loading}
      contentContainerStyle={styles.content}
      style={[styles.container, style]}
      {...otherProps}
    >
      <FormField label={t('Workspace type')} style={[styles.field, styles.workspaceType]}>
        <WorkspaceTypeSelect
          style={styles.select}
          placeholder={t('Any workspace type')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} workspace types selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.workspaceTypeId}
          onChange={(value) => updateFilter('workspaceTypeId', value as number[])}
        />
      </FormField>
      <FormField label={t('Floor')} style={[styles.field, styles.floor]}>
        <FloorSelect
          companyId={companyId}
          style={styles.select}
          groupBuilding
          placeholder={t('Any floor')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} floors selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.floorId}
          onChange={(value) => updateFilter('floorId', value as number[])}
        />
      </FormField>
      <FormField label={t('Installed')} style={[styles.field, styles.installed]}>
        <Select
          style={styles.select}
          placeholder={t('Installed or not installed')}
          items={[
            { value: true, title: t('Installed') },
            { value: false, title: t('Not installed') },
          ]}
          value={filter?.isInstalled}
          onSelect={(value) => updateFilter('isInstalled', value)}
          testID="WorkspaceFilter.InstalledSelect"
        />
      </FormField>
      <FormField label={t('Occupancy')} style={[styles.field, styles.occupancy]}>
        <ProductOccupancySelect
          style={styles.select}
          placeholder={t('Any status')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} statuses selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.occupancyState}
          onChange={(value) => updateFilter('occupancyState', value as number[])}
        />
      </FormField>
      <FormField label={t('Connectivity')} style={[styles.field, styles.connectivity]}>
        <ProductConnectivitySelect
          style={styles.select}
          placeholder={t('Any status')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} statuses selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.connectivityState}
          onChange={(value) => updateFilter('connectivityState', value as number[])}
        />
      </FormField>
      <FormField label={t('Peripherals')} style={[styles.field, styles.peripherals]}>
        <ProductPeripheralsSelect
          style={styles.select}
          placeholder={t('Any status')}
          multiple
          renderValue={(currentValue) => (
            <Text>{t('{{count}} statuses selected', { count: (currentValue as number[])?.length })}</Text>
          )}
          value={filter?.peripheralsState}
          onChange={(value) => updateFilter('peripheralsState', value as number[])}
        />
      </FormField>
    </FilterPanel>
  )
}
interface SkeletonProps extends ViewProps {}

WorkspaceFilter.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return <FilterPanel.Skeleton style={[styles.container, style]} {...otherProps} />
}

export default WorkspaceFilter
